/**
 * SecureEmbed Insights Page
 *
 * Tier 1a - Browse and manage insights with charts, evidence bundles, compliance coverage, and provenance
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Lightbulb, Search, Filter, RefreshCw, ChevronDown,
  Clock, AlertCircle, Activity, Loader2,
  ChevronRight, Package, Download, FileText, GitBranch,
  ShieldCheck, Database, ChevronUp
} from 'lucide-react';
import {
  PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { insightsApi, evidenceBundleApi, EvidenceBundle } from '../services/api';
import { RECHARTS_THEME, CHART_COLORS } from '../constants/colors';
import { format, parseISO } from 'date-fns';
import ChartCard from '../components/ChartCard';
import TierGate from '../components/TierGate';
import ProvenanceChain from '../components/ProvenanceChain';
import InfoTooltip from '../components/InfoTooltip';
import RelatedPagesCard from '../components/RelatedPagesCard';
import RelatedConnections from '../components/RelatedConnections';
import { provenanceApi, ProvenanceRecord } from '../services/api';

interface Insight {
  insight_id: string;
  insight_type: string;
  created_at: string;
  source_embedding_id?: string;
  region?: string;
  datasource?: string;
  value?: number;
  description?: string;
  significance?: 'critical' | 'high' | 'medium' | 'low';
  provenance?: {
    algorithm?: string;
    confidence?: number;
  };
  content?: any;
}

const INSIGHT_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'anomaly_detection', label: 'Anomaly Detection' },
  { value: 'pattern_recognition', label: 'Pattern Recognition' },
  { value: 'cluster_analysis', label: 'Cluster Analysis' },
  { value: 'similarity_score', label: 'Similarity Score' },
  { value: 'trend_analysis', label: 'Trend Analysis' },
  { value: 'correlation', label: 'Correlation' },
  { value: 'aggregation', label: 'Aggregation' },
];

const DATASOURCE_OPTIONS = [
  { value: '', label: 'All Sources' },
  { value: 'aemo', label: 'AEMO' },
  { value: 'aemo-duid', label: 'AEMO DUID' },
  { value: 'aemo-fcas', label: 'AEMO FCAS' },
  { value: 'bom', label: 'BOM' },
  { value: 'aer', label: 'AER' },
  { value: 'regulatory', label: 'Regulatory' },
  { value: 'scholarly', label: 'Scholarly' },
  { value: 'partner', label: 'Partner & Quantum' },
];

const TYPE_COLORS: Record<string, string> = {
  anomaly_detection: '#EF4444',
  pattern_recognition: '#3B82F6',
  cluster_analysis: '#8B5CF6',
  similarity_score: '#10B981',
  trend_analysis: '#F59E0B',
  correlation: '#14B8A6',
  aggregation: '#6366F1',
};

const getTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    'anomaly_detection': 'bg-red-100 text-red-700',
    'pattern_recognition': 'bg-blue-100 text-blue-700',
    'cluster_analysis': 'bg-purple-100 text-purple-700',
    'similarity_score': 'bg-green-100 text-green-700',
    'trend_analysis': 'bg-amber-100 text-amber-700',
    'correlation': 'bg-teal-100 text-teal-700',
    'aggregation': 'bg-indigo-100 text-indigo-700',
  };
  return colors[type] || 'bg-gray-100 text-gray-700';
};

// --- #16: NER Compliance Categories ---

interface ComplianceCategory {
  id: string;
  chapter: string;
  title: string;
  keywords: string[];
}

const NER_COMPLIANCE_CATEGORIES: ComplianceCategory[] = [
  { id: 'ch2-registration', chapter: 'Chapter 2', title: 'Participant Registration', keywords: ['registration', 'registered participant', 'participant category'] },
  { id: 'ch3-market', chapter: 'Chapter 3', title: 'Market Rules & Bidding', keywords: ['market', 'bidding', 'dispatch', 'settlement', 'spot price', 'trading'] },
  { id: 'ch4-security', chapter: 'Chapter 4', title: 'Power System Security', keywords: ['security', 'frequency', 'voltage', 'system strength', 'contingency'] },
  { id: 'ch4a-reliability', chapter: 'Chapter 4A', title: 'System Reliability', keywords: ['reliability', 'reserve', 'rert', 'supply adequacy'] },
  { id: 'ch5-network', chapter: 'Chapter 5', title: 'Network Connection & Planning', keywords: ['network', 'connection', 'planning', 'augmentation', 'access standard'] },
  { id: 'ch5a-retail', chapter: 'Chapter 5A', title: 'Electricity Connection - Retail', keywords: ['retail', 'connection service', 'customer'] },
  { id: 'ch6-distribution', chapter: 'Chapter 6', title: 'Distribution Regulation', keywords: ['distribution', 'dnsp', 'revenue determination'] },
  { id: 'ch6a-transmission', chapter: 'Chapter 6A', title: 'Transmission Regulation', keywords: ['transmission', 'tnsp', 'revenue cap', 'pricing methodology'] },
  { id: 'ch7-metering', chapter: 'Chapter 7', title: 'Metering', keywords: ['metering', 'meter', 'metering data', 'metering installation'] },
  { id: 'ch8-admin', chapter: 'Chapter 8', title: 'Administrative Functions', keywords: ['administrative', 'aer', 'aemc', 'aemo', 'governance'] },
];

type CoverageStatus = 'covered' | 'partial' | 'none';

const getCoverageForCategory = (
  category: ComplianceCategory,
  bundles: EvidenceBundle[],
  insights: Insight[]
): { status: CoverageStatus; matchCount: number; matchingBundleIds: string[] } => {
  let matchCount = 0;
  const matchingBundleIds: string[] = [];

  for (const bundle of bundles) {
    const text = bundle.title.toLowerCase();
    if (category.keywords.some(kw => text.includes(kw))) {
      matchCount++;
      matchingBundleIds.push(bundle.id);
    }
  }

  for (const insight of insights) {
    const text = `${insight.description || ''} ${insight.insight_type}`.toLowerCase();
    if (category.keywords.some(kw => text.includes(kw))) {
      matchCount++;
    }
  }

  const status: CoverageStatus = matchCount >= 3 ? 'covered' : matchCount >= 1 ? 'partial' : 'none';
  return { status, matchCount, matchingBundleIds };
};

// Collapsible provenance chain wrapper
const ProvenanceCollapsible: React.FC<{ loading: boolean; chain: ProvenanceRecord[] }> = ({ loading, chain }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <GitBranch className="w-4 h-4 text-purple-500" />
          Provenance Chain
          {chain.length > 0 && <span className="text-xs text-gray-400 font-normal">{chain.length} stages</span>}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-4 py-3">
          {loading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading provenance...</span>
            </div>
          ) : chain.length > 0 ? (
            <ProvenanceChain chain={chain} />
          ) : (
            <p className="text-sm text-gray-400">No provenance data available</p>
          )}
        </div>
      )}
    </div>
  );
};

const Insights: React.FC = () => {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedDatasource, setSelectedDatasource] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [selectedInsight, setSelectedInsight] = useState<Insight | null>(null);
  const [activeTab, setActiveTab] = useState<'list' | 'bundles' | 'compliance'>('list');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // Evidence bundles
  const [bundles, setBundles] = useState<EvidenceBundle[]>([]);
  const [bundlesLoading, setBundlesLoading] = useState(false);

  // Provenance in modal
  const [modalChain, setModalChain] = useState<ProvenanceRecord[]>([]);
  const [chainLoading, setChainLoading] = useState(false);

  const fetchInsights = useCallback(async (token?: string) => {
    try {
      setLoading(true);
      setError(null);

      const result = await insightsApi.listInsights({
        insight_type: selectedType || undefined,
        limit: 20,
        next_token: token,
      });

      if (result.success && result.data) {
        if (token) {
          setInsights(prev => [...prev, ...result.data.insights]);
        } else {
          setInsights(result.data.insights);
        }
        setNextToken(result.data.next_token || null);
      } else {
        throw new Error('Failed to fetch insights');
      }
    } catch (err: any) {
      console.error('Insights error:', err);
      setError(err.message || 'Failed to load insights. Please ensure the environment is deployed.');
      setInsights([]);
    } finally {
      setLoading(false);
    }
  }, [selectedType]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  const loadBundles = useCallback(async () => {
    setBundlesLoading(true);
    try {
      const result = await evidenceBundleApi.list();
      if (result?.success && result?.data?.bundles) {
        setBundles(result.data.bundles);
      }
    } catch (err: any) {
      console.warn('Bundles load failed:', err.message);
    } finally {
      setBundlesLoading(false);
    }
  }, []);

  const loadProvenance = useCallback(async (embeddingId: string) => {
    setChainLoading(true);
    setModalChain([]);
    try {
      const result = await provenanceApi.getChain(embeddingId);
      if (result?.success && result?.data?.chain) {
        setModalChain(result.data.chain);
      }
    } catch {
      // Provenance not available
    } finally {
      setChainLoading(false);
    }
  }, []);

  // #10: Client-side filtering by datasource and search query
  const filteredInsights = insights.filter(insight => {
    if (selectedDatasource && insight.datasource !== selectedDatasource) return false;
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      insight.insight_id.toLowerCase().includes(query) ||
      insight.insight_type.toLowerCase().includes(query) ||
      insight.description?.toLowerCase().includes(query) ||
      insight.region?.toLowerCase().includes(query) ||
      insight.datasource?.toLowerCase().includes(query) ||
      insight.provenance?.algorithm?.toLowerCase().includes(query)
    );
  });

  const formatDate = (dateStr: string): string => {
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy HH:mm');
    } catch {
      return dateStr;
    }
  };

  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 0.9) return 'text-green-600';
    if (confidence >= 0.7) return 'text-amber-600';
    return 'text-red-600';
  };

  const getSignificanceBadge = (sig?: string): { label: string; className: string } => {
    switch (sig) {
      case 'critical': return { label: 'Critical', className: 'bg-red-100 text-red-800 border border-red-200' };
      case 'high': return { label: 'High', className: 'bg-orange-100 text-orange-800 border border-orange-200' };
      case 'medium': return { label: 'Medium', className: 'bg-yellow-100 text-yellow-800 border border-yellow-200' };
      case 'low': return { label: 'Low', className: 'bg-gray-100 text-gray-600 border border-gray-200' };
      default: return { label: 'Unknown', className: 'bg-gray-100 text-gray-500' };
    }
  };

  // Chart data
  const typeDistribution = insights.reduce((acc, i) => {
    acc[i.insight_type] = (acc[i.insight_type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const pieData = Object.entries(typeDistribution).map(([type, count]) => ({
    name: type.replace(/_/g, ' '),
    value: count,
    fill: TYPE_COLORS[type] || '#6B7280',
  }));

  // Timeline data (group by date)
  const timelineMap = insights.reduce((acc, i) => {
    const day = i.created_at.split('T')[0];
    acc[day] = (acc[day] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const timelineData = Object.entries(timelineMap)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, count]) => ({
      date: new Date(date).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' }),
      count,
    }));

  return (
    <TierGate requiredTier={1.5} tasterContent={
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Insights</h1>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
              <div className="h-4 bg-gray-200 rounded mb-3 w-1/2" />
              <div className="h-3 bg-gray-100 rounded mb-2" />
              <div className="h-3 bg-gray-100 rounded w-3/4" />
            </div>
          ))}
        </div>
      </div>
    }>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Insights</h1>
            <p className="text-gray-500 mt-1">Browse and analyze generated insights</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                showFilters ? 'bg-[#2B064F] text-white border-[#2B064F]' : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
            <button
              onClick={() => fetchInsights()}
              disabled={loading}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Charts */}
        {insights.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Type Distribution" tooltipId="insights.chart.type-distribution">
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={85}
                    dataKey="value"
                    nameKey="name"
                  >
                    {pieData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={RECHARTS_THEME.tooltip.contentStyle} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-2 mt-2">
                {pieData.map(d => (
                  <span key={d.name} className="flex items-center gap-1 text-xs text-gray-600">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.fill }} />
                    {d.name} ({d.value})
                  </span>
                ))}
              </div>
            </ChartCard>

            {timelineData.length > 1 && (
              <ChartCard title="Insights Timeline" tooltipId="insights.chart.timeline">
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={timelineData}>
                    <CartesianGrid {...RECHARTS_THEME.grid} />
                    <XAxis dataKey="date" tick={RECHARTS_THEME.label} />
                    <YAxis tick={RECHARTS_THEME.label} />
                    <Tooltip contentStyle={RECHARTS_THEME.tooltip.contentStyle} />
                    <Area type="monotone" dataKey="count" stroke={CHART_COLORS.primary} fill={CHART_COLORS.primary + '20'} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>
        )}

        {/* Filters - #10: Added datasource dropdown */}
        {showFilters && (
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search insights..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2B064F]"
                />
              </div>
              <div className="relative">
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="w-full appearance-none px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2B064F] pr-10"
                >
                  {INSIGHT_TYPES.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              <div className="relative">
                <select
                  value={selectedDatasource}
                  onChange={(e) => setSelectedDatasource(e.target.value)}
                  className="w-full appearance-none px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2B064F] pr-10"
                >
                  {DATASOURCE_OPTIONS.map(ds => (
                    <option key={ds.value} value={ds.value}>{ds.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        )}

        {/* Tabs - #16: Added Compliance Coverage tab */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
          <button
            onClick={() => setActiveTab('list')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <Lightbulb className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Insights ({filteredInsights.length})
          </button>
          <button
            onClick={() => { setActiveTab('bundles'); if (bundles.length === 0) loadBundles(); }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'bundles' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <Package className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Evidence Bundles
            <InfoTooltip tooltipId="insights.evidence-bundle" />
          </button>
          <button
            onClick={() => { setActiveTab('compliance'); if (bundles.length === 0) loadBundles(); }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'compliance' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <ShieldCheck className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Compliance Coverage
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-800">Deployment Required</h4>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Insights List Tab */}
        {activeTab === 'list' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {loading && insights.length === 0 ? (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 text-[#2B064F] animate-spin mx-auto mb-4" />
                <p className="text-gray-500">Loading insights...</p>
              </div>
            ) : filteredInsights.length === 0 ? (
              <div className="p-12 text-center">
                <Lightbulb className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No insights found</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredInsights.map((insight) => (
                  <div
                    key={insight.insight_id}
                    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedInsight(insight);
                      setModalChain([]);
                      if (insight.source_embedding_id) {
                        loadProvenance(insight.source_embedding_id);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(insight.insight_type)} inline-flex items-center gap-0.5`}>
                            {insight.insight_type.replace(/_/g, ' ')}
                            <InfoTooltip tooltipId="insights.row.type" />
                          </span>
                          {insight.significance && (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-0.5 ${getSignificanceBadge(insight.significance).className}`}>
                              {getSignificanceBadge(insight.significance).label}
                              <InfoTooltip tooltipId="insights.row.significance" />
                            </span>
                          )}
                          {insight.region && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                              {insight.region}
                            </span>
                          )}
                          {/* #10: Source badge */}
                          {insight.datasource && (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-50 text-gray-600 inline-flex items-center gap-0.5">
                              <Database className="w-3 h-3" />
                              {insight.datasource.toUpperCase()}
                            </span>
                          )}
                          {insight.provenance?.confidence && (
                            <span className={`text-xs font-medium inline-flex items-center gap-0.5 ${getConfidenceColor(insight.provenance.confidence)}`}>
                              {(insight.provenance.confidence * 100).toFixed(0)}% confidence
                              <InfoTooltip tooltipId="insights.row.confidence" />
                            </span>
                          )}
                        </div>
                        {insight.description && (
                          <p className="text-sm text-gray-700 mb-2 leading-relaxed">{insight.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {formatDate(insight.created_at)}
                          </span>
                          {insight.provenance?.algorithm && (
                            <span className="flex items-center gap-1">
                              <Activity className="w-3.5 h-3.5" />
                              {insight.provenance.algorithm}
                            </span>
                          )}
                          {insight.value != null && (
                            <span className="font-medium text-gray-600">${insight.value.toFixed(1)}/MWh</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {nextToken && (
              <div className="p-4 border-t border-gray-100 text-center">
                <button
                  onClick={() => fetchInsights(nextToken)}
                  disabled={loading}
                  className="px-4 py-2 text-[#2B064F] hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Evidence Bundles Tab */}
        {activeTab === 'bundles' && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            {bundlesLoading ? (
              <div className="p-12 text-center">
                <Loader2 className="w-8 h-8 text-[#2B064F] animate-spin mx-auto mb-4" />
                <p className="text-gray-500">Loading evidence bundles...</p>
              </div>
            ) : bundles.length === 0 ? (
              <div className="p-12 text-center">
                <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No evidence bundles found</p>
                <p className="text-sm text-gray-400 mt-1">Create bundles from insight detail view</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {bundles.map(bundle => (
                  <div key={bundle.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{bundle.title}</h4>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                          <span>{bundle.insight_count} insights</span>
                          <span>{new Date(bundle.created_at).toLocaleDateString()}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            bundle.status === 'complete' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {bundle.status}
                          </span>
                        </div>
                      </div>
                      {bundle.download_url && (
                        <a href={bundle.download_url} className="p-2 hover:bg-gray-100 rounded-lg">
                          <Download className="w-4 h-4 text-gray-600" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* #16: Compliance Coverage Tab */}
        {activeTab === 'compliance' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-purple-600" />
                  NER Compliance Coverage Matrix
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Coverage assessment based on evidence bundles and insights mapped to National Electricity Rules chapters
                </p>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Chapter</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Obligation Area</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Status</th>
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Evidence Count</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {NER_COMPLIANCE_CATEGORIES.map(category => {
                    const coverage = getCoverageForCategory(category, bundles, insights);
                    const statusConfig = {
                      covered: { label: 'Covered', bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
                      partial: { label: 'Partial', bg: 'bg-amber-100', text: 'text-amber-800', dot: 'bg-amber-500' },
                      none: { label: 'No Evidence', bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
                    }[coverage.status];
                    const isExpanded = expandedCategory === category.id;
                    return (
                      <React.Fragment key={category.id}>
                        <tr
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                          onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-700">{category.chapter}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{category.title}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`} />
                              {statusConfig.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 text-center font-medium">
                            {coverage.matchCount}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={4} className="px-4 py-3 bg-gray-50">
                              {coverage.matchingBundleIds.length > 0 ? (
                                <div className="space-y-2">
                                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Related Evidence Bundles</p>
                                  {bundles
                                    .filter(b => coverage.matchingBundleIds.includes(b.id))
                                    .map(bundle => (
                                      <div key={bundle.id} className="flex items-center gap-3 p-2 bg-white rounded-lg border border-gray-200">
                                        <Package className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-gray-900 truncate">{bundle.title}</p>
                                          <p className="text-xs text-gray-500">{bundle.insight_count} insights - {new Date(bundle.created_at).toLocaleDateString()}</p>
                                        </div>
                                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                                          bundle.status === 'complete' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                                        }`}>
                                          {bundle.status}
                                        </span>
                                      </div>
                                    ))}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500">
                                  <p>No evidence bundles directly mapped to this obligation area.</p>
                                  <p className="text-xs mt-1">Create evidence bundles from the Insights tab that cover: {category.keywords.join(', ')}</p>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Coverage Summary */}
            <div className="grid grid-cols-3 gap-4">
              {(['covered', 'partial', 'none'] as CoverageStatus[]).map(status => {
                const count = NER_COMPLIANCE_CATEGORIES.filter(
                  cat => getCoverageForCategory(cat, bundles, insights).status === status
                ).length;
                const config = {
                  covered: { label: 'Covered', bg: 'bg-green-50 border-green-200', text: 'text-green-700' },
                  partial: { label: 'Partial Coverage', bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700' },
                  none: { label: 'No Evidence', bg: 'bg-red-50 border-red-200', text: 'text-red-700' },
                }[status];
                return (
                  <div key={status} className={`rounded-xl p-4 border ${config.bg}`}>
                    <p className={`text-2xl font-bold ${config.text}`}>{count}</p>
                    <p className={`text-sm ${config.text}`}>{config.label}</p>
                    <p className="text-xs text-gray-500 mt-1">of {NER_COMPLIANCE_CATEGORIES.length} categories</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Insight Detail Modal */}
        {selectedInsight && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-auto">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl">
                <h2 className="text-xl font-bold text-gray-900">Insight Details</h2>
                <button
                  onClick={() => setSelectedInsight(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
                >
                  &times;
                </button>
              </div>

              <div className="p-6 space-y-4">
                {/* Row 1: ID | Type | Algorithm | Date — headings above values, equally spaced */}
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                      ID <InfoTooltip tooltipId="insights.modal.id" />
                    </div>
                    <span className="font-mono text-sm text-gray-700">{selectedInsight.insight_id.replace('insight-', '')}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                      Type <InfoTooltip tooltipId="insights.modal.type" />
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium inline-block ${getTypeColor(selectedInsight.insight_type)}`}>
                      {selectedInsight.insight_type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                      Algorithm <InfoTooltip tooltipId="insights.modal.algorithm" />
                    </div>
                    <span className="flex items-center gap-1 text-sm text-gray-700">
                      <Activity className="w-3.5 h-3.5 text-gray-400" />
                      {selectedInsight.provenance?.algorithm || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                      Date <InfoTooltip tooltipId="insights.modal.date" />
                    </div>
                    <span className="flex items-center gap-1 text-sm text-gray-600">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      {formatDate(selectedInsight.created_at)}
                    </span>
                  </div>
                </div>

                {/* Row 2: Source | Confidence | Rating | Value — headings above values, equally spaced */}
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                      Source <InfoTooltip tooltipId="insights.modal.source" />
                    </div>
                    <span className="flex items-center gap-1 text-sm text-gray-700">
                      <Database className="w-3.5 h-3.5 text-gray-400" />
                      <span className="font-medium truncate">{selectedInsight.datasource?.toUpperCase() || selectedInsight.region || 'N/A'}</span>
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                      Confidence <InfoTooltip tooltipId="insights.modal.confidence" />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#2B064F] rounded-full transition-all"
                          style={{ width: `${(selectedInsight.provenance?.confidence || 0) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-gray-900 shrink-0">
                        {((selectedInsight.provenance?.confidence || 0) * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                      Rating
                    </div>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium inline-block ${getSignificanceBadge(selectedInsight.significance).className}`}>
                      {getSignificanceBadge(selectedInsight.significance).label}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-1 text-xs text-gray-400 mb-1">
                      Value
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {selectedInsight.value != null ? `$${selectedInsight.value.toFixed(2)}/MWh` : 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Region badge */}
                {selectedInsight.region && (
                  <div>
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                      {selectedInsight.region}
                    </span>
                  </div>
                )}

                {/* Description */}
                {selectedInsight.description && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-800 leading-relaxed">{selectedInsight.description}</p>
                  </div>
                )}

                {/* Related Connections */}
                <RelatedConnections
                  query={`${selectedInsight.insight_type} ${selectedInsight.region || ''} ${selectedInsight.description?.split(' ').slice(0, 6).join(' ') || ''}`}
                  maxPerGroup={3}
                  maxGroups={3}
                  label="Related Connections"
                />

                {/* Provenance Chain — collapsed */}
                {selectedInsight.source_embedding_id && (
                  <ProvenanceCollapsible
                    loading={chainLoading}
                    chain={modalChain}
                  />
                )}

                {/* Actions */}
                <div className="border-t border-gray-100 pt-4 flex gap-3">
                  <button
                    onClick={async () => {
                      try {
                        await evidenceBundleApi.create([selectedInsight.insight_id]);
                        await loadBundles();
                        setSelectedInsight(null);
                        setActiveTab('bundles');
                      } catch (err: any) {
                        console.error('Bundle creation failed:', err.message);
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-[#2B064F] text-white rounded-lg text-sm font-medium hover:bg-[#3D0A6B] transition-colors"
                  >
                    <Package className="w-4 h-4" />
                    Create Evidence Bundle
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
                    <FileText className="w-4 h-4" />
                    Export Detail
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <RelatedPagesCard currentRoute="/insights" />
    </TierGate>
  );
};

export default Insights;
